import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, TextField, MenuItem, Select, FormControl } from '@mui/material';
import { ClinicDialog } from '../dialogs/ClinicDialog';
import { CLINIC_DIALOG_SX } from '../../constants/popupStyles';
import { wellnessItemService, WellnessItem } from '../../services/wellnessItemService';
import { WellnessItemEntry } from '../../types/wellnessItemEntry';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  editingEntry: WellnessItemEntry | null;
  allWellnessItems: WellnessItem[];
  onSave: (entry: WellnessItemEntry) => void;
}

const WellnessItemEntryDialog: React.FC<Props> = ({
  open, onClose, title, editingEntry, allWellnessItems, onSave,
}) => {
  const [wellnessItemId, setWellnessItemId] = useState<number | ''>('');
  const [entryTitle, setEntryTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [duration, setDuration] = useState('');
  const [pictureFileId, setPictureFileId] = useState<number | null>(null);
  const [picturePreviewUrl, setPicturePreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setWellnessItemId(editingEntry?.wellness_item_id ?? '');
    setEntryTitle(editingEntry?.title || '');
    setSubtitle(editingEntry?.subtitle || '');
    setDuration(editingEntry?.duration || '');
    const fid = editingEntry?.picture_file_id ?? null;
    setPictureFileId(fid);
    setPicturePreviewUrl(null);
    if (fid) {
      wellnessItemService.getPictureBlobUrl(fid).then(setPicturePreviewUrl).catch(() => {});
    }
  }, [open, editingEntry]);

  const handlePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { file_id } = await wellnessItemService.uploadPicture(file);
      setPictureFileId(file_id);
      setPicturePreviewUrl(URL.createObjectURL(file));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      _key: editingEntry?._key ?? Math.random().toString(36).slice(2),
      wellness_item_id: wellnessItemId !== '' ? Number(wellnessItemId) : null,
      title: entryTitle.trim() || null,
      subtitle: subtitle.trim() || null,
      duration: duration.trim() || null,
      picture_file_id: pictureFileId,
    });
  };

  return (
    <ClinicDialog
      open={open}
      onClose={onClose}
      title={title}
      closeButtonSide="left"
      onSubmit={handleSubmit}
      actions={
        <>
          <Button type="submit" variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
            {editingEntry ? 'Update' : 'Add'}
          </Button>
          <Button onClick={onClose} sx={CLINIC_DIALOG_SX.cancelButton}>Cancel</Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 400 }}>
        <Box sx={CLINIC_DIALOG_SX.fieldRow}>
          <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Item</Typography>
          <FormControl fullWidth size="small">
            <Select
              value={wellnessItemId}
              onChange={(e) => {
                const id = e.target.value as number | '';
                setWellnessItemId(id);
                const selected = allWellnessItems.find((w) => w.id === id);
                if (selected) {
                  setEntryTitle(selected.data?.title || '');
                  setSubtitle(selected.data?.subtitle || '');
                }
              }}
              displayEmpty
              sx={{ background: (CLINIC_DIALOG_SX.textField as any).background }}
            >
              <MenuItem value=""><em>Select an item...</em></MenuItem>
              {allWellnessItems.map((w) => (
                <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={CLINIC_DIALOG_SX.fieldRow}>
          <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Title</Typography>
          <TextField value={entryTitle} onChange={(e) => setEntryTitle(e.target.value)} fullWidth size="small"
            inputProps={{ dir: 'rtl' }}
            InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
        </Box>
        <Box sx={CLINIC_DIALOG_SX.fieldRow}>
          <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Subtitle</Typography>
          <TextField value={subtitle} onChange={(e) => setSubtitle(e.target.value)} fullWidth size="small" multiline rows={3}
            inputProps={{ dir: 'rtl' }}
            InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
        </Box>
        <Box sx={CLINIC_DIALOG_SX.fieldRow}>
          <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Duration</Typography>
          <TextField value={duration} onChange={(e) => setDuration(e.target.value)} fullWidth size="small"
            placeholder="e.g. 4 minutes"
            inputProps={{ dir: 'rtl' }}
            InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
        </Box>
        <Box sx={CLINIC_DIALOG_SX.fieldRow}>
          <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Picture</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
            {picturePreviewUrl && (
              <Box component="img" src={picturePreviewUrl} alt="preview"
                sx={{ width: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
            )}
            <Button component="label" variant="outlined" size="small" disabled={uploading}>
              {uploading ? 'Uploading...' : picturePreviewUrl ? 'Change Picture' : 'Upload Picture'}
              <input type="file" hidden accept="image/*" onChange={handlePictureChange} />
            </Button>
          </Box>
        </Box>
      </Box>
    </ClinicDialog>
  );
};

export default WellnessItemEntryDialog;
